import { challengeFromBank, pickBankItem } from "./bank";
import { ALL_GLOSSES, glossDistractors, glossesOf, gistOf, mutateMeaning, passageText } from "./derive";
import { gateById } from "./gates";
import { hash, seededShuffle } from "./rng";
import { cleanTranslation, truncate } from "./text";
import type { Analect, Challenge, ChallengeOption, GateId } from "./types";

/**
 * Build a challenge for one gate of one passage.
 *
 * Order of preference:
 *   1. an authored bank item naming this passage,
 *   2. an authored item for one of the passage's concepts,
 *   3. a question derived from 楊伯峻's notes or from minimal-pair mutation.
 *
 * `variant` rotates eligible authored items and changes option order.
 * Sparse banks can still repeat a question; coverage is audited separately.
 */
export function makeChallenge(
  analect: Analect,
  gate: GateId,
  difficulty = 0.4,
  variant = 0,
): Challenge {
  const seed = hash(`${analect.id}:${gate}:${Math.round(difficulty * 10)}:v${variant}`);

  const picked = pickBankItem(analect, gate, { variant, mastery: difficulty });
  if (picked) return challengeFromBank(picked.item, seed, picked.route);

  return deriveChallenge(analect, gate, difficulty, variant, seed);
}

function shuffled(options: ChallengeOption[], seed: number): ChallengeOption[] {
  return seededShuffle(options, seed);
}

/** 訓詁: which reading does 楊伯峻 adopt for this word? */
function glossChallenge(analect: Analect, seed: number, variant: number): Challenge | null {
  const glosses = glossesOf(analect);
  if (!glosses.length) return null;
  const target = glosses[variant % glosses.length];

  const why: Record<string, string> = {};
  const options: ChallengeOption[] = [{ id: "answer", text: target.reading }];

  if (target.rejected) {
    options.push({ id: "rej", text: target.rejected });
    why.rej = `楊伯峻注中明確提到這一解法並不採用——這正是最容易走錯的一步。`;
  }
  for (const [index, text] of glossDistractors(target, seed, 4 - options.length).entries()) {
    const id = `d${index}`;
    options.push({ id, text });
    why[id] = `這是《論語》中另一個字的訓解，不是「${target.term}」在本章的意思。`;
  }
  if (options.length < 3) return null;

  return {
    gate: "meaning",
    prompt: gateById("meaning").prompt,
    stem: `本章「${target.term}」字，楊伯峻取的是哪一個意思？`,
    context: passageText(analect),
    options: shuffled(options, seed),
    answerId: "answer",
    evidence: `「${target.term}」——${target.reading}。${target.rejected ? `注中並指出「${target.rejected}」之說不從。` : ""}`,
    difficulty: 0.5,
    source: "gloss",
    purpose: `辨明本章關鍵字「${target.term}」的確切訓解`,
    cognitive: "understand",
    optionWhy: why,
  };
}

/** 誤解識別 from the notes: which reading does the annotation reject? */
function rejectedReadingChallenge(analect: Analect, seed: number): Challenge | null {
  const withRejection = glossesOf(analect).filter((gloss) => gloss.rejected);
  if (!withRejection.length) return null;
  const target = withRejection[0];

  const why: Record<string, string> = {};
  const options: ChallengeOption[] = [{ id: "answer", text: target.rejected! }];
  options.push({ id: "right", text: target.reading });
  why.right = `這正是楊伯峻採用的解釋，不是被否定的那一個。`;
  for (const [index, text] of glossDistractors(target, seed ^ 0x5bf03635, 2).entries()) {
    const id = `d${index}`;
    options.push({ id, text });
    why[id] = `這是別處的訓解，本章注中並未討論它。`;
  }
  if (options.length < 3) return null;

  return {
    gate: "misread",
    prompt: gateById("misread").prompt,
    stem: `關於本章的「${target.term}」，下面哪一種解釋是楊伯峻注中明白不取的？`,
    context: passageText(analect),
    options: shuffled(options, seed),
    answerId: "answer",
    evidence: `注中指出「${target.rejected}」之說不妥，當作「${target.reading}」。`,
    difficulty: 0.6,
    source: "gloss",
    purpose: `認出注釋明確排除的舊解，避免沿用成說`,
    cognitive: "evaluate",
    optionWhy: why,
  };
}

/**
 * 句意辨析 by minimal pair: the distractors are the correct translation with
 * its meaning altered, so character overlap cannot identify the answer.
 */
function minimalPairChallenge(analect: Analect, gate: GateId, seed: number, variant: number): Challenge | null {
  const answer = gistOf(analect);
  if (answer.length < 10) return null;
  const mutations = mutateMeaning(answer, seed + variant, 3);
  if (mutations.length < 2) return null;

  const why: Record<string, string> = {};
  const options: ChallengeOption[] = [{ id: "answer", text: answer }];
  mutations.forEach((mutation, index) => {
    const id = `m${index}`;
    options.push({ id, text: mutation.text });
    why[id] = mutation.why;
  });

  return {
    gate,
    prompt: gateById(gate).prompt,
    stem: "下面哪一項準確表達了本章的意思？",
    context: passageText(analect),
    options: shuffled(options, seed),
    answerId: "answer",
    evidence: truncate(cleanTranslation(analect.translation), 150),
    difficulty: 0.55,
    source: "generated",
    purpose: "逐字比對相近的說法，辨出意思被改動的地方",
    cognitive: "analyze",
    optionWhy: why,
  };
}

/**
 * Last resort — pair the passage with translations of other passages. Kept
 * only because every gate must always be playable; it is the weakest item
 * type in the game and the bank is what replaces it.
 */
function recognitionChallenge(analect: Analect, gate: GateId, seed: number): Challenge {
  const answer = truncate(cleanTranslation(analect.translation), 120);
  const why: Record<string, string> = {};
  const options: ChallengeOption[] = [{ id: "answer", text: answer }];

  const pool = seededShuffle(
    ALL_GLOSSES.filter((gloss) => gloss.analectId !== analect.id),
    seed,
  ).slice(0, 12);
  for (const [index, gloss] of pool.entries()) {
    if (options.length >= 4) break;
    const id = `g${index}`;
    const text = `此章重在說明：${gloss.reading}`;
    if (options.some(option => option.text === text)) continue;
    options.push({ id, text });
    why[id] = "這一項出自別章的訓解，與本章所論不是同一件事。";
  }
  while (options.length < 3) {
    options.push({ id: `f${options.length}`, text: `此章重在說明：${gistOf(analect).slice(0, 12)}以外的另一件事` });
    why[`f${options.length - 1}`] = "這一項並非本章所論。";
  }

  return {
    gate,
    prompt: gateById(gate).prompt,
    stem: "下面哪一項是本章的意思？",
    context: passageText(analect),
    options: shuffled(options, seed),
    answerId: "answer",
    evidence: cleanTranslation(analect.translation),
    difficulty: 0.4,
    source: "generated",
    purpose: "確認本章的基本語意",
    cognitive: "understand",
    optionWhy: why,
  };
}

function deriveChallenge(
  analect: Analect,
  gate: GateId,
  _difficulty: number,
  variant: number,
  seed: number,
): Challenge {
  if (gate === "meaning") {
    return (
      glossChallenge(analect, seed, variant) ??
      minimalPairChallenge(analect, gate, seed, variant) ??
      recognitionChallenge(analect, gate, seed)
    );
  }
  if (gate === "misread") {
    return (
      rejectedReadingChallenge(analect, seed) ??
      minimalPairChallenge(analect, gate, seed, variant) ??
      recognitionChallenge(analect, gate, seed)
    );
  }
  // 致用 and 通貫 have no faithful passage-local derivation — the authored
  // bank reaches them through concepts, and this only runs if that failed.
  return (
    minimalPairChallenge(analect, gate, seed, variant) ??
    recognitionChallenge(analect, gate, seed)
  );
}
